import type { Block } from "@/lib/api";
import type { ChapterColour } from "@/lib/themes/look";
import { testIds } from "@/testIds";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface OutlinePanelProps {
  blocks: Block[];
  onScrollToBlock: (blockId: string) => void;
  /** Index: each heading in a chapter → the chapter's colour. The chapter's
   *  own heading shows its number on that colour, so the outline is the same
   *  index as the PDF (handoff 1d); the number keeps it readable without
   *  colour. Absent for every other theme. */
  chapterColours?: ReadonlyMap<string, ChapterColour>;
}

const OUTLINE_BLOCK_TYPES = [
  "heading",
  "equation",
  "figure",
  "table",
  "theorem",
  "code",
  "abstract",
  "bibliography",
  // A CV's structure (the user, 11 Oct: a CV's outline said "Headings will
  // appear here" with every section on screen): the person, the sections like
  // headings, the entries one step in.
  "personalInfo",
  "cvSection",
  "cvEntry",
];

const BADGE_COLORS: Record<string, string> = {
  heading: "text-primary",
  equation: "text-violet-600",
  figure: "text-emerald-600",
  table: "text-amber-600",
  theorem: "text-rose-600",
  code: "text-sky-600",
  abstract: "text-teal-600",
  bibliography: "text-orange-600",
  personalInfo: "text-primary",
  cvSection: "text-primary",
  cvEntry: "text-primary",
};

/** Indent level in the outline: headings by their level, a CV entry under its section. */
function outlineLevel(block: Block): number {
  if (block.type === "heading") return ((block.content?.level as number) || 1);
  if (block.type === "cvEntry") return 2;
  return 1;
}

function getBlockLabel(block: Block): { badge: string; text: string } {
  const content = block.content ?? {};

  switch (block.type) {
    case "heading": {
      const level = (content.level as number) || 1;
      return {
        badge: `H${level}`,
        text: (content.text as string) || "Untitled",
      };
    }
    case "equation":
      return {
        badge: "Eq",
        text: (content.label as string) || "Equation",
      };
    case "figure":
      return {
        badge: "Fig",
        text: (content.caption as string) || "Figure",
      };
    case "table":
      return {
        badge: "Tbl",
        text: (content.caption as string) || "Table",
      };
    case "theorem": {
      const theoremType = (content.theoremType as string) || "Theorem";
      const name = content.name as string;
      return {
        badge: "Thm",
        text: name ? `${theoremType}: ${name}` : theoremType,
      };
    }
    case "code":
      return {
        badge: "</>",
        text: (content.language as string) || "Code",
      };
    case "abstract":
      return { badge: "Abs", text: "Abstract" };
    case "bibliography":
      return { badge: "Bib", text: "Bibliography" };
    case "personalInfo":
      return { badge: "CV", text: (content.name as string) || "Your name" };
    case "cvSection":
      return { badge: "§", text: (content.title as string) || "Section" };
    case "cvEntry": {
      const role = (content.role as string) || "";
      const org = (content.org as string) || "";
      return { badge: "•", text: [role, org].filter(Boolean).join(", ") || "Entry" };
    }
    default:
      return { badge: "?", text: block.type };
  }
}

export function OutlinePanel({ blocks, onScrollToBlock, chapterColours }: OutlinePanelProps) {
  const { t } = useTranslation('panels');
  const outlineBlocks = blocks.filter((b) =>
    OUTLINE_BLOCK_TYPES.includes(b.type),
  );

  if (outlineBlocks.length === 0) {
    return (
      <div data-testid="outline-empty" className="text-center py-6 px-4 text-muted-foreground text-[13px]">
        <p>{t('outline.noBlocks')}</p>
        <p className="text-xs mt-1 opacity-70">
          {t('outline.noBlocksHint')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="outline-panel" className="flex flex-col gap-0.5">
      {outlineBlocks.map((block) => {
        const { badge, text } = getBlockLabel(block);
        const isHeading = block.type === "heading";
        const level = isHeading || block.type === "cvEntry"
          ? outlineLevel(block)
          : 0;
        const paddingLeft = level > 0 ? (level - 1) * 16 + 8 : 8;
        const colorClass = BADGE_COLORS[block.type] ?? "text-primary";
        const chapter = isHeading && level === 1 ? chapterColours?.get(block.id) : undefined;

        return (
          <button
            key={block.id}
            data-testid="outline-item"
            data-block-id={block.id}
            data-block-type={block.type}
            className="flex items-center gap-2 py-1.5 px-2 border-none bg-none text-left rounded cursor-pointer transition-colors w-full hover:bg-[var(--lilia-bg)]"
            style={{ paddingLeft: `${paddingLeft}px` }}
            onClick={() => onScrollToBlock(block.id)}
            title={text}
          >
            {chapter ? (
              <span
                data-testid={testIds.look.outlineChip}
                data-colour={chapter.colour}
                className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded-[5px] text-[10.5px] font-bold text-white shrink-0"
                style={{ background: chapter.colour }}
              >
                {chapter.label}
              </span>
            ) : (
              <span
                className={`text-[10px] font-semibold font-mono min-w-[20px] shrink-0 ${colorClass}`}
              >
                {badge}
              </span>
            )}
            <span className="text-[13px] text-foreground overflow-hidden text-ellipsis whitespace-nowrap">
              {text}
            </span>
          </button>
        );
      })}
    </div>
  );
}
